import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lock, Building2, Smartphone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const paymentSchema = z.object({
  provider: z.enum(["orange_money", "mpesa_drc", "airtel_money"], {
    required_error: "Veuillez sélectionner un mode de paiement"
  }),
  customer_phone: z.string()
    .regex(/^(\+243|0)\d{9}$/, "Numéro de téléphone invalide (ex: +243xxxxxxxxx)"),
  customer_name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  customer_email: z.string().email("Email invalide (requis pour recevoir le lien)"),
});

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("mobile_money"); // "mobile_money" or "bank_transfer"
  const [siteContent, setSiteContent] = useState(null);

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      provider: "orange_money",
      customer_phone: "",
      customer_name: "",
      customer_email: ""
    }
  });

  const selectedProvider = watch("provider");

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setSiteContent(response.data);
      } catch (error) {
        console.error("Erreur lors du chargement du contenu", error);
      }
    };
    fetchContent();
  }, []);

  const providers = [
    { value: "orange_money", label: "Orange Money", color: "border-orange-500 bg-orange-50" },
    { value: "mpesa_drc", label: "M-Pesa (Vodacom)", color: "border-emerald-500 bg-emerald-50" },
    { value: "airtel_money", label: "Airtel Money", color: "border-red-500 bg-red-50" }
  ];

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments`, {
        ...values,
        amount_cdf: 10000
      });

      const { internal_txn_id, redirect_url, payment_instructions } = response.data;

      if (payment_instructions) {
        toast.success(payment_instructions, { duration: 6000 });
      }

      if (redirect_url) {
        window.location.href = redirect_url;
      } else {
        navigate(`/payment/${internal_txn_id}`);
      }
    } catch (error) {
      const message = error?.response?.data?.detail || "Erreur lors de la création du paiement";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>

        <Card className="shadow-xl" data-testid="checkout-form">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              <CardTitle className="text-3xl">Accès sécurisé</CardTitle>
            </div>
            <CardDescription className="text-base">
              Pasteur Joseph Kalala - Le Mystère de la Croix - eBook numérique
            </CardDescription>
            <div className="pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">Total</span>
                <span className="text-2xl font-bold text-primary">10,000 CDF</span>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-blue-800">
                <strong>🔒 Accès payant uniquement.</strong> Après paiement, vous recevrez le lien de téléchargement par email.
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
              <p className="text-sm text-green-800">
                ✅ <strong>25 personnes</strong> ont déjà acheté ce livre
              </p>
            </div>
          </CardHeader>

          <CardContent>
            {/* Payment Method Selection */}
            <div className="mb-6 space-y-3">
              <Label className="text-lg font-semibold">Choisissez votre mode de paiement</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Mobile Money Option */}
                <div
                  onClick={() => setPaymentMethod("mobile_money")}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    paymentMethod === "mobile_money"
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-6 h-6 text-orange-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Mobile Money</h3>
                      <p className="text-sm text-gray-600">Orange, M-Pesa, Airtel</p>
                      <p className="text-xs text-green-600 font-medium mt-1">✓ Automatique</p>
                    </div>
                  </div>
                </div>

                {/* Bank Transfer Option */}
                {siteContent?.bank_transfer_enabled && (
                  <div
                    onClick={() => setPaymentMethod("bank_transfer")}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      paymentMethod === "bank_transfer"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="w-6 h-6 text-blue-600" />
                      <div>
                        <h3 className="font-semibold text-gray-900">Virement Bancaire</h3>
                        <p className="text-sm text-gray-600">{siteContent?.bank_name}</p>
                        <p className="text-xs text-amber-600 font-medium mt-1">⏱ Manuel (24-48h)</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Money Form */}
            {paymentMethod === "mobile_money" && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-3">
                  <Label>Mode de paiement Mobile Money</Label>
                  <RadioGroup
                    value={selectedProvider}
                    onValueChange={(value) => setValue("provider", value)}
                    className="space-y-3"
                  >
                    {providers.map((provider) => (
                      <div
                        key={provider.value}
                        className={`flex items-center space-x-3 border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          selectedProvider === provider.value
                            ? provider.color
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        onClick={() => setValue("provider", provider.value)}
                        data-testid={`provider-${provider.value}`}
                      >
                        <RadioGroupItem value={provider.value} id={provider.value} />
                        <Label htmlFor={provider.value} className="flex-1 cursor-pointer font-medium">
                          {provider.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {errors.provider && (
                    <p className="text-sm text-red-600">{errors.provider.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer_name">Nom complet *</Label>
                  <Input
                    id="customer_name"
                    {...register("customer_name")}
                    placeholder="Jean Kabongo"
                    data-testid="customer-name-input"
                  />
                  {errors.customer_name && (
                    <p className="text-sm text-red-600">{errors.customer_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer_email">
                    Email * <span className="text-sm text-gray-500">(pour recevoir le lien de téléchargement)</span>
                  </Label>
                  <Input
                    id="customer_email"
                    type="email"
                    {...register("customer_email")}
                    placeholder="jean@exemple.com"
                    data-testid="customer-email-input"
                  />
                  {errors.customer_email && (
                    <p className="text-sm text-red-600">{errors.customer_email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer_phone">Numéro de téléphone Mobile Money *</Label>
                  <Input
                    id="customer_phone"
                    {...register("customer_phone")}
                    placeholder="+243xxxxxxxxx"
                    data-testid="customer-phone-input"
                  />
                  {errors.customer_phone && (
                    <p className="text-sm text-red-600">{errors.customer_phone.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full cta-button text-white py-6 text-lg rounded-full"
                  disabled={loading}
                  data-testid="submit-payment-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Traitement en cours...
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 h-5 w-5" />
                      Payer 10,000 CDF et accéder à l'ebook
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* Bank Transfer Instructions */}
            {paymentMethod === "bank_transfer" && siteContent && (
              <div className="space-y-6">
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                  <h3 className="font-bold text-lg text-blue-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Coordonnées bancaires
                  </h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-semibold text-gray-700">Banque:</span>
                      <span className="font-bold text-gray-900">{siteContent.bank_name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-semibold text-gray-700">Numéro de compte:</span>
                      <span className="font-bold text-gray-900">{siteContent.bank_account_number}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-semibold text-gray-700">Titulaire:</span>
                      <span className="font-bold text-gray-900">{siteContent.bank_account_holder}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="font-semibold text-gray-700">Montant:</span>
                      <span className="font-bold text-orange-600">10,000 CDF</span>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                  <h4 className="font-bold text-amber-900 mb-2">📝 Instructions importantes:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-amber-900">
                    <li>Faites le virement de <strong>10,000 CDF</strong> vers le compte ci-dessus</li>
                    <li><strong>IMPORTANT:</strong> Mettez votre numéro de téléphone comme référence de paiement</li>
                    <li>Prenez une photo ou capture d'écran de la preuve de paiement</li>
                    <li>Envoyez la preuve par WhatsApp à: <strong>+243 808 408 407</strong></li>
                    <li>Vous recevrez l'accès au livre dans les 24-48 heures après vérification</li>
                  </ol>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    💡 <strong>Astuce:</strong> Pour un accès immédiat, utilisez le paiement Mobile Money qui est automatique!
                  </p>
                </div>

                <Button
                  onClick={() => navigate("/")}
                  size="lg"
                  variant="outline"
                  className="w-full py-6 text-lg"
                >
                  J'ai compris, retour à l'accueil
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutPage;